import { type FormEvent, useState } from "react";

type ConfirmDeleteDialogProps = {
	open: boolean;
	tenantName: string;
	tenantSlug: string;
	onClose: () => void;
	onDeleted: () => void;
};

export function ConfirmDeleteDialog({
	open,
	tenantName,
	tenantSlug,
	onClose,
	onDeleted,
}: ConfirmDeleteDialogProps) {
	const [input, setInput] = useState("");
	const [error, setError] = useState("");
	const [loading, setLoading] = useState(false);

	if (!open) return null;

	async function handleSubmit(e: FormEvent) {
		e.preventDefault();
		setError("");
		setLoading(true);
		try {
			const res = await fetch(`/api/admin/tenants/${tenantSlug}`, {
				method: "DELETE",
				credentials: "include",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ confirmName: input }),
			});
			if (!res.ok) {
				const body = await res.json().catch(() => null);
				setError(
					(body as { error?: string } | null)?.error ??
						`Delete failed (${res.status})`,
				);
				return;
			}
			setInput("");
			onDeleted();
		} catch {
			setError("Network error");
		} finally {
			setLoading(false);
		}
	}

	function handleClose() {
		setInput("");
		setError("");
		onClose();
	}

	return (
		<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
			<div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
				<h2 className="text-lg font-semibold text-gray-900 mb-2">
					Delete Tenant
				</h2>
				<p className="text-sm text-gray-600 mb-4">
					Type <span className="font-semibold">{tenantName}</span> to confirm
					deletion. This action is irreversible.
				</p>
				{error && <p className="text-red-600 text-sm mb-3">{error}</p>}
				<form onSubmit={handleSubmit} className="space-y-4">
					<input
						type="text"
						value={input}
						onChange={(e) => setInput(e.target.value)}
						placeholder={tenantName}
						className="block w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-red-500 focus:outline-none"
					/>
					<div className="flex justify-end gap-2">
						<button
							type="button"
							onClick={handleClose}
							className="rounded px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
						>
							Cancel
						</button>
						<button
							type="submit"
							disabled={input !== tenantName || loading}
							className="rounded bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
						>
							{loading ? "Deleting…" : "Delete"}
						</button>
					</div>
				</form>
			</div>
		</div>
	);
}
